import { useEffect, useRef } from "react";

import { isEditableTarget } from "@/app/editorShortcuts";

interface UseKeyboardNavOptions {
  cameraRef: React.RefObject<{ x: number; y: number; zoom: number }>;
  draw: () => void;
  onCursorUpdate: () => void;
  spaceHeldRef: React.MutableRefObject<boolean>;
  isLocked?: boolean;
}

type PanDirection = "left" | "right" | "up" | "down";

function panDirectionForCode(code: string): PanDirection | null {
  switch (code) {
    case "ArrowLeft":
    case "KeyA":
      return "left";
    case "ArrowRight":
    case "KeyD":
      return "right";
    case "ArrowUp":
    case "KeyW":
      return "up";
    case "ArrowDown":
    case "KeyS":
      return "down";
    default:
      return null;
  }
}

export function useKeyboardNav({
  cameraRef,
  draw,
  onCursorUpdate,
  spaceHeldRef,
  isLocked = false,
}: UseKeyboardNavOptions) {
  const arrowStateRef = useRef<Record<PanDirection, boolean>>({
    left: false,
    right: false,
    up: false,
    down: false,
  });
  const isLockedRef = useRef(isLocked);

  // Stable refs so the rAF loop and listeners always see latest callbacks
  const drawRef = useRef(draw);
  const onCursorUpdateRef = useRef(onCursorUpdate);

  useEffect(() => {
    drawRef.current = draw;
    onCursorUpdateRef.current = onCursorUpdate;
  }, [draw, onCursorUpdate]);

  useEffect(() => {
    isLockedRef.current = isLocked;
  }, [isLocked]);

  useEffect(() => {
    if (!isLocked) {
      return;
    }

    spaceHeldRef.current = false;
    arrowStateRef.current = { left: false, right: false, up: false, down: false };
    onCursorUpdateRef.current();
  }, [isLocked, spaceHeldRef]);

  // Keyboard listeners
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (isEditableTarget(e.target)) return;
      const panDir = panDirectionForCode(e.code);
      // WASD shouldn't trigger panning while a modifier is held, so existing
      // shortcuts like Cmd/Ctrl+S still work.
      const isWasd = panDir !== null && e.code.startsWith("Key");
      const wasdBlocked = isWasd && (e.metaKey || e.ctrlKey || e.altKey);
      const activePanDir = wasdBlocked ? null : panDir;
      if (isLockedRef.current) {
        if (e.code === "Space" || activePanDir !== null) {
          e.preventDefault();
        }
        return;
      }
      if (e.code === "Space" && !e.repeat) {
        e.preventDefault();
        spaceHeldRef.current = true;
        onCursorUpdateRef.current();
      }
      if (activePanDir !== null) {
        e.preventDefault();
        arrowStateRef.current[activePanDir] = true;
      }
    }
    function onKeyUp(e: KeyboardEvent) {
      const panDir = panDirectionForCode(e.code);
      if (isLockedRef.current) {
        if (e.code === "Space") {
          spaceHeldRef.current = false;
          onCursorUpdateRef.current();
        }
        if (panDir !== null) {
          arrowStateRef.current = { left: false, right: false, up: false, down: false };
        }
        return;
      }
      if (e.code === "Space") {
        spaceHeldRef.current = false;
        onCursorUpdateRef.current();
      }
      if (panDir !== null) {
        arrowStateRef.current[panDir] = false;
      }
    }
    function onBlur() {
      spaceHeldRef.current = false;
      onCursorUpdateRef.current();
      arrowStateRef.current = { left: false, right: false, up: false, down: false };
    }
    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
  }, [spaceHeldRef]);

  // Arrow key panning loop
  useEffect(() => {
    const ARROW_SPEED = 8;
    let frameId: number | null = null;

    function tick() {
      if (isLockedRef.current) {
        frameId = requestAnimationFrame(tick);
        return;
      }

      const a = arrowStateRef.current;
      const dx = (a.right ? ARROW_SPEED : 0) - (a.left ? ARROW_SPEED : 0);
      const dy = (a.down ? ARROW_SPEED : 0) - (a.up ? ARROW_SPEED : 0);
      if (dx !== 0 || dy !== 0) {
        cameraRef.current.x -= dx;
        cameraRef.current.y -= dy;
        drawRef.current();
      }
      frameId = requestAnimationFrame(tick);
    }
    frameId = requestAnimationFrame(tick);
    return () => {
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { spaceHeldRef };
}
